/* =========================================================================
   Custom Bundle Builder - Candy Cow
   - Bundle size selector (3 / 5 / 7 etc., configured via section blocks)
   - Empty card slots that customers fill with products
   - Desktop: HTML5 drag and drop from carousel onto slots
   - Mobile: tap a slot to open a product picker modal
   - Live progress bar + bundle price + savings display
   - On complete: add all items to cart with shared bundle properties
     and open Dawn's native cart drawer. If a discount code is configured
     on the active bundle size, applies it via /discount/{code} redirect
     fallback when needed.
   ========================================================================= */

(function () {
  const MOBILE_BREAKPOINT = 750;
  const isMobile = () => window.matchMedia('(max-width: ' + (MOBILE_BREAKPOINT - 1) + 'px)').matches;

  // Format cents as money using the section's money_format
  const formatMoney = (cents, format) => {
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      try { return window.Shopify.formatMoney(cents, format); } catch (e) { /* fall through */ }
    }
    const value = (cents / 100).toFixed(2);
    return '$' + value;
  };

  class BundleBuilder {
    constructor(root) {
      this.root = root;
      this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
      this.successMessage = root.dataset.successMessage || 'Bundle added to cart!';
      this.errorMessage = root.dataset.errorMessage || 'Sorry, we couldn’t add your bundle.';

      // Step 1: size buttons
      this.sizeButtons = Array.from(root.querySelectorAll('[data-bundle-size]'));

      // Step 2: slots, progress, summary
      this.slotsEl = root.querySelector('[data-bundle-slots]');
      this.progressFill = root.querySelector('[data-bundle-progress-fill]');
      this.progressCount = root.querySelector('[data-bundle-progress-count]');
      this.progressTotal = root.querySelector('[data-bundle-progress-total]');
      this.priceEl = root.querySelector('[data-bundle-total-price]');
      this.comparePriceEl = root.querySelector('[data-bundle-compare-price]');
      this.savingsEl = root.querySelector('[data-bundle-savings]');
      this.checkoutBtn = root.querySelector('[data-bundle-checkout]');
      this.checkoutSpinner = this.checkoutBtn && this.checkoutBtn.querySelector('.bundle-builder__checkout-spinner');

      // Step 3: products carousel
      this.productsTrack = root.querySelector('[data-bundle-products-track]');
      this.productsViewport = root.querySelector('[data-bundle-products-viewport]');
      this.prevBtn = root.querySelector('[data-bundle-products-prev]');
      this.nextBtn = root.querySelector('[data-bundle-products-next]');
      this.productEls = Array.from(root.querySelectorAll('[data-bundle-product]'));

      // Modal (mobile picker)
      this.modal = root.querySelector('[data-bundle-modal]');
      this.modalCloseEls = Array.from(root.querySelectorAll('[data-bundle-modal-close]'));
      this.modalPickEls = Array.from(root.querySelectorAll('[data-bundle-modal-pick]'));

      // Toast
      this.toast = root.querySelector('[data-bundle-toast]');

      // State
      this.activeSize = 0;
      this.activeBundlePrice = 0;       // in cents
      this.activeComparePrice = 0;      // in cents
      this.activeDiscountCode = '';
      this.slots = [];                  // [{ productId, variantId, title, image, handle } | null]
      this.dragData = null;             // { product, sourceIndex (or null) }
      this.activeSlotIndex = null;      // for mobile modal
      this.isSubmitting = false;
      this.dragGhost = null;

      this.init();
    }

    init() {
      // Set up size buttons
      this.sizeButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.selectSize(btn));
      });

      // Pick first size by default (or the one marked active in markup)
      const initialBtn = this.sizeButtons.find((b) => b.classList.contains('is-active')) || this.sizeButtons[0];
      if (initialBtn) this.selectSize(initialBtn, { skipReset: true });

      // Product carousel arrows
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollCarousel(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollCarousel(1));
      if (this.productsTrack) {
        this.productsTrack.addEventListener('scroll', () => this.updateArrowState(), { passive: true });
      }
      this.updateArrowState();

      // Drag and drop on products (desktop)
      this.productEls.forEach((el) => {
        el.addEventListener('dragstart', (e) => this.onProductDragStart(e, el));
        el.addEventListener('dragend', (e) => this.onProductDragEnd(e, el));

        // "Add" button on product card — fills next empty slot
        const addBtn = el.querySelector('[data-bundle-add]');
        if (addBtn) {
          addBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.addToNextEmptySlot(this.readProductData(el));
          });
        }
      });

      // Modal interactions
      if (this.modal) {
        this.modalCloseEls.forEach((el) => {
          el.addEventListener('click', () => this.closeModal());
        });
        this.modalPickEls.forEach((el) => {
          el.addEventListener('click', () => {
            const product = this.readProductData(el);
            if (this.activeSlotIndex !== null) {
              this.setSlot(this.activeSlotIndex, product);
              this.closeModal();
            }
          });
        });
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.modal && !this.modal.hidden) this.closeModal();
        });
      }

      // Checkout
      if (this.checkoutBtn) {
        this.checkoutBtn.addEventListener('click', () => this.completeBundle());
      }
    }

    // === Size selection ==================================================
    selectSize(btn, opts = {}) {
      this.sizeButtons.forEach((b) => {
        b.classList.toggle('is-active', b === btn);
        b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
      });

      this.activeSize = parseInt(btn.dataset.bundleSize, 10) || 0;
      this.activeBundlePrice = parseInt(btn.dataset.sizePrice, 10) || 0;
      this.activeComparePrice = parseInt(btn.dataset.sizeComparePrice, 10) || 0;
      this.activeDiscountCode = (btn.dataset.sizeDiscountCode || '').trim();

      if (!opts.skipReset) {
        // Preserve existing picks when shrinking; truncate when smaller
        if (this.slots.length > this.activeSize) {
          this.slots = this.slots.slice(0, this.activeSize);
        }
      }
      // Ensure slots array length matches size
      while (this.slots.length < this.activeSize) this.slots.push(null);

      this.renderSlots();
      this.updateProgress();
      this.updateSummary();
    }

    // === Slots rendering =================================================
    renderSlots() {
      if (!this.slotsEl) return;
      this.slotsEl.innerHTML = '';

      for (let i = 0; i < this.activeSize; i++) {
        const slot = this.slots[i];
        const el = document.createElement('div');
        el.className = 'bundle-builder__slot';
        el.setAttribute('role', 'listitem');
        el.dataset.bundleSlotIndex = String(i);

        if (slot) {
          el.classList.add('is-filled');
          el.innerHTML = `
            <div class="bundle-builder__slot-filled">
              <div class="bundle-builder__slot-image-wrap">
                ${slot.image
                  ? `<img class="bundle-builder__slot-image" src="${slot.image}" alt="${this.escapeHtml(slot.title)}" loading="lazy" draggable="false">`
                  : ''}
              </div>
              <div class="bundle-builder__slot-title">${this.escapeHtml(slot.title)}</div>
              <button type="button" class="bundle-builder__slot-remove" aria-label="Remove ${this.escapeHtml(slot.title)}" data-bundle-slot-remove>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"></line>
                  <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"></line>
                </svg>
              </button>
            </div>
          `;
        } else {
          el.innerHTML = `
            <div class="bundle-builder__slot-placeholder">
              <div class="bundle-builder__slot-number">${i + 1}</div>
              <div class="bundle-builder__slot-text">${isMobile() ? 'Tap to add' : 'Drop here'}</div>
            </div>
          `;
        }

        this.attachSlotHandlers(el, i);
        this.slotsEl.appendChild(el);
      }
    }

    attachSlotHandlers(el, index) {
      // Click to remove or open mobile picker
      el.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-bundle-slot-remove]');
        if (removeBtn) {
          e.stopPropagation();
          this.setSlot(index, null);
          return;
        }

        if (isMobile()) {
          this.openModal(index);
        } else if (!this.slots[index]) {
          // Desktop click on an empty slot — also fill next available product as a fallback
          // (drag-drop is primary, but click is a friendly nudge)
        }
      });

      // Drag and drop targets
      el.addEventListener('dragover', (e) => {
        if (!this.dragData) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        el.classList.add('is-drop-target');
      });

      el.addEventListener('dragleave', () => {
        el.classList.remove('is-drop-target');
      });

      el.addEventListener('drop', (e) => {
        e.preventDefault();
        el.classList.remove('is-drop-target');
        if (!this.dragData) return;
        this.setSlot(index, this.dragData.product);
      });
    }

    setSlot(index, product) {
      if (index < 0 || index >= this.activeSize) return;
      this.slots[index] = product;
      this.renderSlots();
      this.updateProgress();
      this.updateSummary();
    }

    addToNextEmptySlot(product) {
      const idx = this.slots.findIndex((s) => s === null);
      if (idx === -1) {
        this.showToast('Your bundle is full! Remove a bag to swap.', { error: false });
        return;
      }
      this.setSlot(idx, product);
    }

    // === Product data helpers ============================================
    readProductData(el) {
      return {
        productId: el.dataset.productId,
        variantId: el.dataset.variantId,
        title: el.dataset.productTitle,
        image: el.dataset.productImage,
        handle: el.dataset.productHandle,
      };
    }

    // === Drag and drop on product cards ==================================
    onProductDragStart(e, el) {
      if (isMobile()) {
        e.preventDefault();
        return;
      }
      this.dragData = { product: this.readProductData(el), sourceIndex: null };
      el.classList.add('is-dragging');

      try {
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData('text/plain', el.dataset.productId || '');
      } catch (err) { /* ignore */ }

      // Custom drag image using the product photo
      this.makeDragGhost(this.dragData.product.image);
      if (this.dragGhost && e.dataTransfer.setDragImage) {
        e.dataTransfer.setDragImage(this.dragGhost, 55, 55);
      }
    }

    onProductDragEnd(e, el) {
      el.classList.remove('is-dragging');
      this.dragData = null;
      this.cleanupDragGhost();
      this.root.querySelectorAll('.bundle-builder__slot.is-drop-target').forEach((s) => {
        s.classList.remove('is-drop-target');
      });
    }

    makeDragGhost(imageUrl) {
      this.cleanupDragGhost();
      const ghost = document.createElement('div');
      ghost.className = 'bundle-builder__drag-ghost';
      if (imageUrl) {
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = '';
        ghost.appendChild(img);
      }
      document.body.appendChild(ghost);
      this.dragGhost = ghost;
    }

    cleanupDragGhost() {
      if (this.dragGhost && this.dragGhost.parentNode) {
        this.dragGhost.parentNode.removeChild(this.dragGhost);
      }
      this.dragGhost = null;
    }

    // === Carousel ========================================================
    scrollCarousel(direction) {
      if (!this.productsTrack) return;
      const firstCard = this.productsTrack.querySelector('[data-bundle-product]');
      const step = firstCard ? firstCard.offsetWidth + 12 : 220;
      const visible = Math.max(1, Math.floor(this.productsTrack.offsetWidth / step));
      this.productsTrack.scrollBy({ left: direction * step * visible, behavior: 'smooth' });
    }

    updateArrowState() {
      if (!this.productsTrack || !this.prevBtn || !this.nextBtn) return;
      const max = this.productsTrack.scrollWidth - this.productsTrack.clientWidth - 1;
      this.prevBtn.disabled = this.productsTrack.scrollLeft <= 0;
      this.nextBtn.disabled = this.productsTrack.scrollLeft >= max;
    }

    // === Progress + summary ==============================================
    updateProgress() {
      const filled = this.slots.filter(Boolean).length;
      const total = this.activeSize;
      const pct = total === 0 ? 0 : (filled / total) * 100;

      if (this.progressFill) this.progressFill.style.width = pct + '%';
      if (this.progressCount) this.progressCount.textContent = String(filled);
      if (this.progressTotal) this.progressTotal.textContent = String(total);
    }

    updateSummary() {
      const filled = this.slots.filter(Boolean).length;
      const complete = filled === this.activeSize && this.activeSize > 0;

      // Price label
      if (this.priceEl) {
        if (this.activeBundlePrice > 0) {
          this.priceEl.textContent = formatMoney(this.activeBundlePrice, this.moneyFormat);
        } else {
          this.priceEl.textContent = '—';
        }
      }

      // Compare-at
      if (this.comparePriceEl) {
        if (this.activeComparePrice > 0 && this.activeComparePrice > this.activeBundlePrice) {
          this.comparePriceEl.textContent = formatMoney(this.activeComparePrice, this.moneyFormat);
          this.comparePriceEl.hidden = false;
        } else {
          this.comparePriceEl.hidden = true;
        }
      }

      // Savings
      if (this.savingsEl) {
        const savings = this.activeComparePrice - this.activeBundlePrice;
        if (savings > 0 && this.activeBundlePrice > 0) {
          this.savingsEl.textContent = 'Save ' + formatMoney(savings, this.moneyFormat);
          this.savingsEl.hidden = false;
        } else {
          this.savingsEl.hidden = true;
        }
      }

      // Checkout button state
      if (this.checkoutBtn) {
        this.checkoutBtn.disabled = !complete || this.isSubmitting;
        this.checkoutBtn.setAttribute('aria-disabled', !complete || this.isSubmitting ? 'true' : 'false');
      }
    }

    // === Modal (mobile) ==================================================
    openModal(slotIndex) {
      if (!this.modal) return;
      this.activeSlotIndex = slotIndex;
      this.modal.hidden = false;
      // Force reflow so transition fires
      void this.modal.offsetHeight;
      this.modal.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      setTimeout(() => {
        if (!this.modal.classList.contains('is-open')) this.modal.hidden = true;
      }, 300);
      this.activeSlotIndex = null;
    }

    // === Add to cart + open cart drawer ==================================
    async completeBundle() {
      if (this.isSubmitting) return;
      const filled = this.slots.filter(Boolean);
      if (filled.length !== this.activeSize || this.activeSize === 0) return;

      this.isSubmitting = true;
      this.setCheckoutLoading(true);
      this.updateSummary();

      // Generate a shared bundle id so the cart can group these line items
      const bundleId = 'bdl_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

      // Group selections by variantId so duplicates become a single line item with quantity
      const grouped = new Map();
      filled.forEach((p, idx) => {
        const key = String(p.variantId);
        if (!grouped.has(key)) {
          grouped.set(key, { variantId: p.variantId, title: p.title, quantity: 0, firstIndex: idx });
        }
        grouped.get(key).quantity += 1;
      });

      const items = Array.from(grouped.values()).map((entry) => ({
        id: parseInt(entry.variantId, 10),
        quantity: entry.quantity,
        properties: {
          _bundle_id: bundleId,
          _bundle_size: String(this.activeSize),
        },
      }));

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          let msg = this.errorMessage;
          try {
            const data = await response.json();
            if (data && (data.description || data.message)) msg = data.description || data.message;
          } catch (parseErr) { /* ignore */ }
          this.showToast(msg, { error: true });
          return;
        }

        // If a discount code is configured for this bundle size, apply it
        // via Shopify's /discount endpoint (fire-and-forget — it sets a cart cookie).
        if (this.activeDiscountCode) {
          try {
            await fetch('/discount/' + encodeURIComponent(this.activeDiscountCode), {
              method: 'GET',
              redirect: 'manual',
              credentials: 'same-origin',
            });
          } catch (discErr) { /* ignore — discount can still be applied at checkout */ }
        }

        // Open Dawn's native cart drawer with refreshed section HTML
        await this.openCartDrawer();

        // Reset slots for next bundle
        this.slots = new Array(this.activeSize).fill(null);
        this.renderSlots();
        this.updateProgress();
        this.updateSummary();

        this.showToast(this.successMessage, { error: false });
      } catch (err) {
        console.error('Bundle Builder add to cart error:', err);
        this.showToast(this.errorMessage, { error: true });
      } finally {
        this.isSubmitting = false;
        this.setCheckoutLoading(false);
        this.updateSummary();
      }
    }

    async openCartDrawer() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        // Fallback: go to cart page
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        return;
      }

      try {
        const sectionsResp = await fetch(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble', {
          cache: 'no-store',
        });
        const sections = await sectionsResp.json();
        cartDrawer.renderContents({ id: null, sections });
      } catch (err) {
        // If section render fails, at least open the drawer
        if (typeof cartDrawer.open === 'function') cartDrawer.open();
      }
    }

    setCheckoutLoading(loading) {
      if (!this.checkoutBtn) return;
      this.checkoutBtn.classList.toggle('is-loading', loading);
      if (this.checkoutSpinner) this.checkoutSpinner.hidden = !loading;
    }

    // === Toast ===========================================================
    showToast(message, opts = {}) {
      if (!this.toast) return;
      this.toast.textContent = message;
      this.toast.classList.toggle('is-error', !!opts.error);
      this.toast.hidden = false;
      // Trigger transition
      void this.toast.offsetHeight;
      this.toast.classList.add('is-visible');

      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        this.toast.classList.remove('is-visible');
        setTimeout(() => {
          if (!this.toast.classList.contains('is-visible')) this.toast.hidden = true;
        }, 250);
      }, 2800);
    }

    // === Utilities =======================================================
    escapeHtml(str) {
      if (str == null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  // Initialise every bundle builder section on the page
  const init = () => {
    document.querySelectorAll('[data-bundle-builder]').forEach((el) => {
      if (el._bundleBuilder) return;
      el._bundleBuilder = new BundleBuilder(el);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init in theme editor when section is added/changed
  document.addEventListener('shopify:section:load', init);
})();
